import { useEffect, useCallback, useRef } from "react";
import {
  refreshAccessToken,
  isTokenExpiringSoon,
  isTokenExpired,
  clearAuthAndRedirect,
} from "../utils/tokenUtils";

/**
 * Hook for visibility-based token refresh.
 * Automatically refreshes the access token when the user returns to the tab
 * and the token is expired or about to expire.
 *
 * @param {Object} options - Configuration options
 * @param {Function} options.onRefreshSuccess - Callback when token refresh succeeds
 * @param {Function} options.onRefreshError - Callback when token refresh fails
 * @returns {Object} - { attemptRefresh: Function }
 */
const useTokenRefresh = ({ onRefreshSuccess, onRefreshError } = {}) => {
  const isRefreshing = useRef(false);

  const attemptRefresh = useCallback(async () => {
    // Prevent concurrent refresh attempts
    if (isRefreshing.current) return;

    // Check if user is logged in
    const token = JSON.parse(localStorage.getItem("token"));
    if (!token?.AccessToken) return;

    // Check if token needs refresh
    if (isTokenExpired() || isTokenExpiringSoon(60000)) {
      isRefreshing.current = true;
      try {
        await refreshAccessToken();
        onRefreshSuccess?.();
      } catch (error) {
        console.error("Token refresh failed:", error);
        onRefreshError?.(error);
        clearAuthAndRedirect();
      } finally {
        isRefreshing.current = false;
      }
    }
  }, [onRefreshSuccess, onRefreshError]);

  // Listen for visibility changes (tab focus)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        attemptRefresh();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [attemptRefresh]);

  return { attemptRefresh };
};

export default useTokenRefresh;
